import type { Screen } from '@ghostscreen/contracts';
import { i18n } from '../../i18n';
import type { OverviewStatusModel } from '../../lib/dashboard/overview/buildOverviewStatus';
import { displayManagePath } from '../displays/manage/displayManageRoutes';
import { playlistPath } from '../playlists/playlistRoutes';
import type { HomePlaylistCard } from './buildHomePlaylistCards';

export type HomeHeroAction = { label: string; to: string };

export type HomeHeroModel = {
  headline: string;
  subline: string;
  action: HomeHeroAction | null;
};

/**
 * Hero copy follows the organisation's next real step: pair a screen, build a
 * playlist, fill it, put it on a screen, then keep everything healthy.
 * Write actions are only offered to people who are allowed to take them.
 */
export function buildHomeHero(input: {
  screens: Screen[];
  assignments: ReadonlyMap<string, string | null>;
  cards: HomePlaylistCard[];
  status: OverviewStatusModel;
  canManage: boolean;
}): HomeHeroModel {
  const { screens, assignments, cards, status, canManage } = input;
  const offer = (action: HomeHeroAction) => (canManage ? action : null);

  if (screens.length === 0) {
    return {
      headline: i18n.t('home:hero.pairScreen.headline'),
      subline: canManage
        ? i18n.t('home:hero.pairScreen.sublineManager')
        : i18n.t('home:hero.pairScreen.sublineViewer'),
      action: offer({ label: i18n.t('home:hero.pairScreen.action'), to: '/screens#setup' }),
    };
  }

  if (cards.length === 0) {
    return {
      headline: i18n.t('home:hero.createPlaylist.headline'),
      subline: canManage
        ? i18n.t('home:hero.createPlaylist.sublineManager')
        : i18n.t('home:hero.createPlaylist.sublineViewer'),
      action: offer({ label: i18n.t('home:hero.createPlaylist.action'), to: '/playlists#new' }),
    };
  }

  const firstFillable = cards.every((card) => card.itemCount === 0) ? cards[0] : undefined;
  if (firstFillable) {
    return {
      headline: i18n.t('home:hero.addContent.headline'),
      subline: i18n.t('home:hero.addContent.subline', { name: firstFillable.name }),
      action: offer({
        label: i18n.t('home:hero.addContent.action'),
        to: playlistPath(firstFillable.id),
      }),
    };
  }

  const anythingAssigned = [...assignments.values()].some(Boolean);
  if (!anythingAssigned) {
    const ready = cards.filter((card) => card.itemCount > 0).length;
    return {
      headline: i18n.t('home:hero.assignPlaylist.headline'),
      subline: i18n.t('home:hero.assignPlaylist.subline', { count: ready }),
      action: offer({ label: i18n.t('home:hero.assignPlaylist.action'), to: '/screens' }),
    };
  }

  const firstIssue = status.issues[0];
  if (firstIssue) {
    return {
      headline: status.headline,
      subline: status.subline,
      action: {
        label:
          status.issues.length === 1
            ? firstIssue.action
            : i18n.t('home:hero.startWith', { name: firstIssue.name }),
        to: displayManagePath(firstIssue.screenId),
      },
    };
  }

  return { headline: status.headline, subline: status.subline, action: null };
}
